import React, { useEffect, useRef, useContext, useState, useMemo, useCallback } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import { ChevronLeftIcon, DocumentTextIcon, CheckCircleIcon } from '@heroicons/react/solid';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { auth } from '../../../firebase';
import { functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../hooks/useAuth';
import { rerenderPatientReportIfStale } from '../../../utils/autoPatientReport';

// ---------------------------------------------------------------------------
// Scale definitions
// ---------------------------------------------------------------------------
// Each entry maps a display label to the Firestore field names already
// present in the shared assessment_data_model (subsection 4 of the default
// qbCheck schema). By reusing the same field names we avoid data-model
// changes and keep compatibility with the existing `sections.7.outputs`
// pipeline.
// ---------------------------------------------------------------------------

const QB_SCALES = [
  {
    label: 'Activity - MicroEvents',
    qScoreField: 'activityQScore',
    percentileField: 'activityPercentile',
  },
  {
    label: 'Impulsivity - Commission Errors',
    qScoreField: 'impulsivityQScore',
    percentileField: 'impulsivityPercentile',
  },
  {
    label: 'Inattention - Omission Errors',
    qScoreField: 'inattentionOmissionErrorsQScore',
    percentileField: 'inattentionOmissionErrorsPercentile',
  },
  {
    label: 'Inattention - Reaction Time',
    qScoreField: 'inattentionReactionErrorsQScore',
    percentileField: 'inattentionReactionErrorsPercentile',
  },
  {
    label: 'Inattention - Reaction Time Variation',
    qScoreField: 'inattentionReactionTimeVarianceQScore',
    percentileField: 'inattentionReactionTimeVariancePercentile',
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
// Simplified QbCheck data-entry form for The Owl Centre. Unlike the full
// Phoenix form (QBCheckForm.js), this variant:
//   - Captures only Q-Scores, Percentiles and the ADHD Total Symptom Score
//   - Percentiles are age/gender-normalised values entered manually by the user
//   - Shows a live preview of the rules-based interpretation sentence
//   - Skips images, significance selectors, free-text interpretation & LLM
//   - Skips the clinician review workflow — auto-approves on submit
//   - Is accessible to Support / Org Admin users (not gated to SSI clinician)
// ---------------------------------------------------------------------------

export default function QBCheckFormTOC() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel } = useContext(PatientDataContext);
  const { claims } = useAuth();

  // ---- local form state ----
  const [formState, setFormState] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isInitialLoad = useRef(true);

  // ---- Firestore section reference ----
  const currentSectionNumber = 1;

  // -----------------------------------------------------------------------
  // Initialise form state from Firestore on first load
  // -----------------------------------------------------------------------
  // If the form was previously submitted, the CF will have persisted the
  // data to Firestore. We read it back here so the form pre-populates,
  // allowing the user to review and update values before re-submitting.
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (!isInitialLoad.current) return;
    const qbData = patientDataModel?.dataModel?.['6_manageReports']?.['qbCheck']?.[currentSectionNumber];
    if (!qbData) return;

    const initial = {};

    // Q-Score / Percentile fields
    QB_SCALES.forEach(({ qScoreField, percentileField }) => {
      initial[qScoreField] = { value: qbData?.[qScoreField]?.answer?.value || '' };
      initial[percentileField] = { value: qbData?.[percentileField]?.answer?.value || '' };
    });

    // ADHD Total Symptom Score
    initial.adhdTotalSymptomScore = {
      value: qbData?.adhdTotalSymptomScore?.answer?.value || '',
    };

    setFormState(initial);
    isInitialLoad.current = false;
  }, [patientDataModel]);

  // -----------------------------------------------------------------------
  // Validation — form is valid when all 11 fields have non-empty values
  // -----------------------------------------------------------------------

  const isFormValid = useMemo(() => {
    const allFields = [
      ...QB_SCALES.flatMap(s => [s.qScoreField, s.percentileField]),
      'adhdTotalSymptomScore',
    ];
    return allFields.every(f => {
      const val = formState[f]?.value;
      return val !== undefined && val !== null && String(val).trim() !== '';
    });
  }, [formState]);

  // -----------------------------------------------------------------------
  // Field change handler
  // -----------------------------------------------------------------------
  // All form data is held in local state only — nothing is written to
  // Firestore until the user submits the form. The backend CF receives the
  // complete payload and performs all Firestore writes using the Admin SDK,
  // which avoids security-rule issues for Support / Org. Admin users who
  // are not the assigned clinician for the patient.
  // -----------------------------------------------------------------------

  const handleFieldChange = useCallback((fieldName, value, fieldType) => {
    if (fieldType === 'qScore') {
      if (value !== '' && !/^-?\d*\.?\d*$/.test(value)) return;
    } else if (fieldType === 'percentile') {
      if (value !== '' && (!/^\d*$/.test(value) || parseInt(value, 10) > 100)) return;
    } else if (fieldType === 'totalScore') {
      if (value !== '' && !/^-?\d*\.?\d*$/.test(value)) return;
    }

    setFormState(prev => ({ ...prev, [fieldName]: { value } }));
  }, []);

  // -----------------------------------------------------------------------
  // Submit flow
  // -----------------------------------------------------------------------
  // 1. Opens a confirmation dialog
  // 2. On confirm: calls `generateQbCheckReport` CF (org-branched, no LLM)
  // 3. On CF success: auto-triggers `TriggerRenderDocTemplate` to produce
  //    the standalone QbCheck PDF
  // 4. Navigates back to the Documents dashboard
  // -----------------------------------------------------------------------

  const handleFinish = () => {
    setIsSaving(true);
    setIsSaving(false);
    setIsDialogOpen(true);
  };

  const handleGenerateReport = async () => {
    setIsDialogOpen(false);
    setIsGenerating(true);

    // Build a flat { fieldName: value } payload from local state so the
    // backend CF can persist everything to Firestore via the Admin SDK.
    const formDataPayload = {};
    Object.entries(formState).forEach(([key, field]) => {
      formDataPayload[key] = field?.value ?? '';
    });

    try {
      const generateReportFunction = httpsCallable(functions, 'generateQbCheckReport');
      const result = await generateReportFunction({
        patientId,
        clinicianId: auth.currentUser?.uid,
        tocFormData: formDataPayload,
      });
      console.log('QbCheck report generated (TOC):', result.data);

      // Fire-and-forget: re-finalise Patient Report if stale.
      // generateQbCheckReport already persisted qbCheckReportApproved(At) and
      // all QbCheck outputs via SERVER_TIMESTAMP — safe to trigger the
      // staleness check immediately. Pattern matches RD_CI, RD_SSI, RD_DD,
      // RD_PAD so the Patient Report row turns yellow at the same instant as
      // QbCheck, rather than waiting for the QbCheck PDF render to complete.
      rerenderPatientReportIfStale({
        patientId,
        clinicianId: auth.currentUser?.uid,
        patientDataModel,
        organisation: claims?.organisation ?? patientDataModel?.organisation,
      }).catch((e) => console.error('rerenderPatientReportIfStale (TOC QbCheck) failed:', e));

      // Fire-and-forget: render the standalone QbCheck PDF in parallel.
      // We call renderAndStoreDocument directly instead of using the
      // TriggerRenderDocTemplate wrapper because the wrapper writes processing
      // status via the client SDK (fails for Support / Org. Admin). The backend
      // sets qbCheckReportProcessingStatus to 'complete' on success.
      const renderFunction = httpsCallable(functions, 'renderAndStoreDocument', {
        timeout: 360000,
      });
      renderFunction({
        report_stage: 'QbCheck',
        clinician_id: auth.currentUser?.uid,
        patient_id: patientId,
      }).then(renderResult => {
        console.log('QbCheck PDF rendered (TOC):', renderResult.data);
      }).catch(renderError => {
        if (renderError.code === 'deadline-exceeded') {
          console.log('Client-side timeout for renderAndStoreDocument — function continues in background');
        } else {
          console.error('Error in renderAndStoreDocument (TOC):', renderError);
        }
      });
    } catch (error) {
      console.error('Error generating QbCheck report (TOC):', error);
      setIsGenerating(false);
    }
  };

  const handleCancelGeneration = () => {
    setIsGenerating(false);
  };

  const handleNextPageButtonClick = () => {
    navigate(`/patient/${patientId}/documents`);
  };

  // ---- breadcrumbs ----
  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}`],
    ['Documents', `/patient/${patientId}/documents`],
    ['QbCheck'],
  ];

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <div className="flex">       
        <div className="flex-grow p-8">

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={() => navigate(`/patient/${patientId}/documents`)}
              className="flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300"
              aria-label="Back to Previous Section"
            >
              <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              <span className="font-bold">Back to Documents</span>
            </button>

             <button
              onClick={handleFinish}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ${isFormValid ? 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              disabled={!isFormValid}
            >
                <span className="font-bold">{isSaving ? "Saving Progress..." : "Submit QbCheck Form"}</span>
                <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />
              </button>
            
          </div>


          {/* Section Title */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">1. QbCheck</h2>
          </div>

          {/* Subsection 1.1 — Q-Scores & Percentiles */}
          <div className="p-8 mb-8 bg-gray-50 rounded-2xl border border-gray-200">
            <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
              1.1 Objective Measures: Q-Scores &amp; Percentiles
            </h3>

            {/* Column headers — aligned over the input columns */}
            <div className="flex items-end mb-4">
              <div className="w-96" />
              <div className="ml-5 flex gap-4">
                <div className="w-44 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Q-Score</div>
                <div className="w-44 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Percentile</div>
              </div>
            </div>

            <div className="space-y-4">
              {QB_SCALES.map((scale, index) => (
                <React.Fragment key={scale.qScoreField}>
                  <div className="mb-4 flex items-center">
                    <div className="flex flex-col w-96">
                      <h4 className="text-left text-lg font-semibold text-gray-700">
                        {scale.label}
                      </h4>
                    </div>

                    <div className="ml-5">
                      <div className="flex gap-4">
                        <div className="w-44">
                          <input
                            type="text"
                            className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                            value={formState[scale.qScoreField]?.value || ''}
                            onChange={(e) => handleFieldChange(scale.qScoreField, e.target.value, 'qScore')}
                            placeholder="e.g. 1.6"
                          />
                        </div>
                        <div className="w-44">
                          <div className="relative">
                            <input
                              type="text"
                              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                              value={formState[scale.percentileField]?.value || ''}
                              onChange={(e) => handleFieldChange(scale.percentileField, e.target.value, 'percentile')}
                              placeholder="0-100"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {index < QB_SCALES.length - 1 && (
                    <div className="border-t border-gray-200 my-4"></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Subsection 1.2 — ADHD Total Symptom Score */}
          <div className="p-8 mb-8 bg-gray-50 rounded-2xl border border-gray-200">
            <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
              1.2 ADHD Total Symptom Score
            </h3>

            <div className="space-y-4">
              <div className="mb-4 flex items-center">
                <div className="flex flex-col w-96">
                  <h4 className="text-left text-lg font-semibold text-gray-700">
                    ADHD Total Symptom Score
                  </h4>
                </div>

                <div className="ml-5">
                  <div className="w-96">
                    <input
                      type="text"
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                      value={formState.adhdTotalSymptomScore?.value || ''}
                      onChange={(e) => handleFieldChange('adhdTotalSymptomScore', e.target.value, 'totalScore')}
                      placeholder="Enter a number"
                    />
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Duplicate bottom Submit button (mirrors QBCheckForm / QBTestForm pattern) */}
          <div className="flex justify-end items-center mt-6">
            <button
              onClick={handleFinish}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ${isFormValid ? 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              disabled={!isFormValid}
            >
              <span className="font-bold">{isSaving ? "Saving Progress..." : "Submit QbCheck Form"}</span>
              <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Confirmation dialog (matches QBCheckForm / QBTestForm styling) */}
        <AlertDialog.Root open={isDialogOpen && !isGenerating} onOpenChange={setIsDialogOpen}>
          <AlertDialog.Portal>
            <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
            <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[600px] max-w-[90vw] max-h-[85vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <AlertDialog.Title className="text-xl font-semibold">
                  QbCheck Form Complete
                </AlertDialog.Title>
                <AlertDialog.Cancel asChild>
                  <button className="absolute top-2 right-3 text-gray-500 hover:text-gray-700">
                    <span aria-hidden>×</span>
                  </button>
                </AlertDialog.Cancel>
              </div>

              <div className="mb-6">
                <h3 className="font-semibold mb-2">Summary:</h3>
                <div className="text-gray-700 space-y-2">
                  <p>The QbCheck Form has been completed. Would you like to generate this section of the report?</p>
                  <ul className="list-disc pl-4 text-sm ml-2 mt-3">
                    {QB_SCALES.map(s => (
                      <li key={s.qScoreField}>
                        {s.label}: Q-Score {formState[s.qScoreField]?.value || '—'}, Percentile {formState[s.percentileField]?.value || '—'}%
                      </li>
                    ))}
                    <li>ADHD Total Symptom Score: {formState.adhdTotalSymptomScore?.value || '—'}</li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <AlertDialog.Cancel asChild>
                  <button
                    onClick={() => setIsDialogOpen(false)}
                    className="py-2 px-4 mb-2 font-semibold border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </AlertDialog.Cancel>
                <AlertDialog.Action asChild>
                  <button
                    onClick={handleGenerateReport}
                    className="flex items-center px-4 py-2 mb-2 font-semibold rounded-md bg-violet-900 text-white hover:bg-violet-800"
                  >
                    <DocumentTextIcon className="w-5 h-5 mr-1" aria-hidden="true" />
                    Generate Report
                  </button>
                </AlertDialog.Action>
              </div>
            </AlertDialog.Content>
          </AlertDialog.Portal>
        </AlertDialog.Root>

        <GenerateReportProgressDialog
          reportStage="QbCheck"
          patientId={patientId}
          clinicianId={auth.currentUser?.uid || ''}
          handleNextPageButtonClick={handleNextPageButtonClick}
          isGenerating={isGenerating}
          onCancel={handleCancelGeneration}
          completionButtonLabel="→ Go to Documents"
        />
      </div>
    </Layout>
  );
}
